import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CardConfig } from '../../notes/card/card.constants';
import { LoggerService } from '../../shared/services/logger.service';
import {
  defaultOverviewCardStatuses,
  KeyValuePair,
  OverviewCardConfig,
  OverviewGroup,
} from './overview-card.constants';
import { checkInputsForText } from '../../design-system.helper';

@Component({
  selector: 'overview-card',
  templateUrl: './overview-card.component.html',
  styleUrls: ['./overview-card.component.scss'],
})
export class OverviewCardComponent implements OnInit, OnChanges {
  @Input() config: OverviewCardConfig;

  /** If we dont have an endpoint in the config then this will be used to provide data to this component */
  @Input() data: any;

  // used to determine if this component is waiting to recieve data from parent
  @Input() isLoading: boolean;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onClick = new EventEmitter<any>();

  @Output() overviewCardsSet: EventEmitter<OverviewGroup[]> = new EventEmitter<
    OverviewGroup[]
  >();

  loading = false;
  error = false;
  message: string;
  groups: OverviewGroup[];
  statuses: KeyValuePair[];
  cardConfig = new CardConfig({
    hideHeader: true,
    hideSubHeader: true,
    hideFooter: true,
  });

  constructor(
    private http: HttpClient,
    private loggerService: LoggerService,
  ) {}

  ngOnInit() {
    const [config, data, isLoading] = checkInputsForText([
      this.config,
      this.data,
      this.isLoading,
    ]);

    this.config = config;
    this.data = data;
    this.isLoading = isLoading;

    this.statuses = this.config.statuses
      ? [...this.config.statuses]
      : [...defaultOverviewCardStatuses];
    this.message = this.config.message || 'No results';
    this.loading = this.isLoading ? this.isLoading : false;

    if (!this.loading) {
      this.data ? this.groupByStatus(this.data) : this.retrieveData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // check if ngOnInit() has been called (ie. if this.statuses is undefined) and
    // if new data has been passed to the component
    if (this.statuses && changes.isLoading && !changes.isLoading.currentValue) {
      this.groupByStatus(this.data);
    }
  }

  retrieveData() {
    this.loading = true;

    this.http.get(this.config.endpoint).subscribe(
      (response: any) => {
        this.groupByStatus(response.results || response.data || []);
      },
      (error: any) => {
        this.loggerService.error(error);
        this.error = true;
        this.overviewCardsSet.emit(null);
        this.loading = false;
      },
    );
  }

  groupByStatus(data) {
    this.overviewCardsSet.emit(data);
    // Generate groups with each available status
    const groups: OverviewGroup[] = this.statuses.map((status) => ({
      status_key: status.key,
      status_value: status.value,
      items: [],
    }));

    data.forEach((item) => {
      const status = item.status;
      const isAvailable =
        this.statuses.map((s) => s.key).indexOf(status) !== -1;
      const currentGroup = groups.find(
        ({ status_key }) => status_key === item.status,
      );

      // Only add items that match available statuses
      if (isAvailable && this.config.hasStatusCount) {
        // use count prop instead of item length
        const countProp = this.config.statusCountProp || 'count';

        currentGroup.count = item[countProp];
      } else if (isAvailable) {
        currentGroup.items.push(item);
      }
    });

    this.groups = [...groups];
    this.loading = false;
  }
}
